import React, { useEffect, useMemo, useState } from 'react';
import { Button, Divider, Modal, Switch, Tooltip, Typography } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ReadOnlyGuard } from '@spark';
import {
  CLASSIC_METRICS_DEFAULTS,
  getMetricsInfoForScope,
} from './classicMetricsDefaults';

const { Text } = Typography;

const Row = ({ title, subtitle, checked, onChange, showInfo = false, disabled = false }) => {
  return (
    <div style={{ padding: '14px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Text style={{ fontSize: 14, fontWeight: 400 }}>{title}</Text>
            {showInfo && (
              <Tooltip title={subtitle}>
                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            )}
          </div>
          <Text style={{ color: '#8c8c8c', fontSize: 14, fontWeight: 300 }}>{subtitle}</Text>
        </div>
        <Switch checked={!!checked} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
};

/**
 * Enable/disable toggles for one metric group (`scope`: 'classic' |
 * 'accuracy'). Both scopes edit the shared `classic_metrics_config` object —
 * the draft starts from the full current value, so saving one scope never
 * clobbers the other scope's toggles.
 */
const ClassicMetricsConfigModal = ({ open, onClose, value, onChange, scope = 'classic' }) => {
  const metricsInfo = useMemo(() => getMetricsInfoForScope(scope), [scope]);
  const initial = useMemo(() => ({ ...CLASSIC_METRICS_DEFAULTS, ...(value || {}) }), [value]);
  const [draft, setDraft] = useState(initial);

  useEffect(() => {
    if (!open) return;
    setDraft(initial);
  }, [open, initial]);

  const setKey = (k, v) => setDraft(prev => ({ ...prev, [k]: v }));

  const save = () => {
    onChange?.(draft);
    onClose?.();
  };

  return (
    <Modal
      open={open}
      footer={null}
      width={520}
      onCancel={onClose}
      closeIcon={false}
      zIndex={3000}
      styles={{ body: {} }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text style={{ fontWeight: 500, fontSize: 16, display: 'block' }}>
            {scope === 'accuracy' ? 'Accuracy Metric Configuration' : 'Classic Metric Configuration'}
          </Text>
          <Text style={{ color: '#8c8c8c', fontSize: 14, fontWeight: 400 }}>
            Enable or disable individual metrics for your evaluation.
          </Text>
        </div>
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
      </div>

      <div style={{ marginTop: 18 }}>
        {metricsInfo.map((m, idx) => (
          <React.Fragment key={m.key}>
            <Row
              title={m.title}
              subtitle={m.subtitle}
              checked={draft[m.switchKey]}
              onChange={(v) => setKey(m.switchKey, v)}
              showInfo={true}
            />
            {idx < metricsInfo.length - 1 && <Divider style={{ margin: 0 }} />}
          </React.Fragment>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 14 }}>
        <Button onClick={onClose}>Cancel</Button>
        <ReadOnlyGuard>
          <Button type="primary" onClick={save}>Ok</Button>
        </ReadOnlyGuard>
      </div>
    </Modal>
  );
};

export default ClassicMetricsConfigModal;

